import { ManagerBlogsIndexBlog } from "@/types/manager_blogs";
import { Badge, DropdownMenu, IconButton, Text } from "@radix-ui/themes";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { EllipsisVertical, ExternalLink, Pencil } from "lucide-react";
import { dateRangeFilter } from "@/shared/tables/utility";
import { badgeColors } from "@/shared/constants/constants";
import BasicTable from "../BasicTable";

interface Props {
  blogs: ManagerBlogsIndexBlog[];
  setSelectedRow: React.Dispatch<
    React.SetStateAction<ManagerBlogsIndexBlog | null>
  >;
}

export default function BlogsTable({ blogs, setSelectedRow }: Props) {
  const [data, setData] = useState(() => [...blogs]);

  useEffect(() => {
    setData([...blogs]);
  }, [blogs]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columnHelper = createColumnHelper<ManagerBlogsIndexBlog>();

  const columns = useMemo<ColumnDef<ManagerBlogsIndexBlog, any>[]>(
    () => [
      columnHelper.accessor("title", {
        header: "Title",
        meta: { filterVariant: "text" },
        cell: (props) => <Text weight="medium">{props.getValue()}</Text>,
      } as AccessorFnColumnDef<ManagerBlogsIndexBlog, any>),
      columnHelper.accessor("author", {
        header: "Author",
        meta: { filterVariant: "text" },
        cell: (props) => <Text>{props.getValue()}</Text>,
      } as AccessorFnColumnDef<ManagerBlogsIndexBlog, any>),
      // Filters on the ISO value and displays the formatted one — the pairing
      // the manager events table uses for `date_raw`.
      columnHelper.accessor("publish_at_raw", {
        header: "Publish date",
        cell: (props) => (
          <Text color={props.row.original.publish_at ? undefined : "gray"}>
            {props.row.original.publish_at ?? "—"}
          </Text>
        ),
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (props) => (
          <Badge size="2" variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "draft", title: "Draft" },
            { value: "scheduled", title: "Scheduled" },
            { value: "published", title: "Published" },
          ],
        },
      } as AccessorFnColumnDef<ManagerBlogsIndexBlog, any>),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (props) => (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton
                variant="ghost"
                size="1"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisVertical size="16px" />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = props.row.original.edit_path;
                }}
              >
                <Pencil size="14px" />
                Edit Post
              </DropdownMenu.Item>
              {/* Absent until the post is actually live — there is nothing to
                  view while it is a draft or still waiting for its date. */}
              {props.row.original.public_path && (
                <DropdownMenu.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = props.row.original.public_path!;
                  }}
                >
                  <ExternalLink size="14px" />
                  View Public Page
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <BasicTable
      table={table}
      handleRowOnClick={(row) => setSelectedRow(row.original)}
    />
  );
}
